"use client";

import { usePathname, useRouter, useSearchParams } from "next/navigation";
import {
    createContext,
    ReactNode,
    useCallback,
    useContext,
    useTransition,
} from "react";

type FilterTransitionContextValue = {
    isPending: boolean;
    startTransition: (callback: () => void) => void;
};

const FilterTransitionContext =
    createContext<FilterTransitionContextValue | null>(null);

export function FilterTransitionProvider({
    children,
}: {
    children: ReactNode;
}) {
    const [isPending, startTransition] = useTransition();
    return (
        <FilterTransitionContext.Provider
            value={{ isPending, startTransition }}
        >
            {children}
        </FilterTransitionContext.Provider>
    );
}

export function useFilterPending() {
    return useContext(FilterTransitionContext)?.isPending ?? false;
}

export function useFilterParams() {
    const router = useRouter();
    const pathname = usePathname();
    const searchParams = useSearchParams();
    const context = useContext(FilterTransitionContext);

    const setParams = useCallback(
        (updates: Record<string, string | null | undefined>) => {
            const params = new URLSearchParams(searchParams.toString());
            let changed = false;
            for (const [key, value] of Object.entries(updates)) {
                if (value) {
                    if (params.get(key) !== value) changed = true;
                    params.set(key, value);
                } else if (params.has(key)) {
                    changed = true;
                    params.delete(key);
                }
            }
            if (!changed) return;

            params.delete("page");

            const nextQuery = params.toString();
            if (nextQuery === searchParams.toString()) return;

            const run = () =>
                router.replace(
                    nextQuery ? `${pathname}?${nextQuery}` : pathname,
                );

            if (context) {
                context.startTransition(run);
            } else {
                run();
            }
        },
        [router, pathname, searchParams, context],
    );

    return { searchParams, setParams };
}
